import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { orderService } from '../../../api/services/orderService';
import { useError } from '../../../contexts/ErrorContext';
import { fetchItemListCached } from '../../../utils/cache/itemCache';
import { fetchLocationListCached } from '../../../utils/cache/locationCache';
import { fetchUomListCached } from '../../../utils/cache/uomCache';

const PAGE_SIZE = 50;

const hasText = (value) => value != null && String(value).trim() !== '';

export const buildProcessActivityArgs = (subOrderId, dyeingNoShort, offset) => {
  const args = { limit: PAGE_SIZE, offset };
  if (hasText(dyeingNoShort)) {
    args.dyeingNoShort = dyeingNoShort;
  } else {
    args.subOrderId = subOrderId;
  }
  return args;
};

const formatLocationLabel = (locationId, locationMap) => {
  if (locationId == null) return '—';
  return locationMap.get(Number(locationId)) || `#${locationId}`;
};

const formatTxTime = (value, language) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const localeMap = { ko: 'ko-KR', en: 'en-US', id: 'id-ID' };
  const locale = localeMap[language] || 'en-US';
  return `${date.toLocaleDateString(locale)} ${date.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit'
  })}`;
};

const buildLookupMaps = async () => {
  const [items, locations, uoms] = await Promise.all([
    fetchItemListCached(false).catch(() => []),
    fetchLocationListCached(false).catch(() => []),
    fetchUomListCached(false).catch(() => [])
  ]);
  const nextItems = new Map();
  (items || []).forEach((item) => {
    const id = item?.item_id ?? item?.id;
    if (id == null) return;
    nextItems.set(Number(id), {
      sku: item.sku || item.item_code || '',
      name: item.name || item.item_name || ''
    });
  });
  const nextLoc = new Map();
  (locations || []).forEach((loc) => {
    const id = loc?.location_id;
    if (id == null) return;
    const name = [loc.warehouse, loc.bin_code].filter(Boolean).join(' - ');
    nextLoc.set(Number(id), name ? `${name} (#${id})` : `#${id}`);
  });
  const nextUom = new Map();
  (uoms || []).forEach((uom) => {
    const id = uom?.uom_id ?? uom?.id;
    if (id == null) return;
    nextUom.set(Number(id), uom.code || uom.name || String(id));
  });
  return { itemMap: nextItems, locationMap: nextLoc, uomMap: nextUom };
};

const applyEvents = (data, setters) => {
  setters.setDyeingEvents(Array.isArray(data?.dyeing_process_event) ? data.dyeing_process_event : []);
  setters.setWindingEvents(Array.isArray(data?.winding_process_event) ? data.winding_process_event : []);
  setters.setEventsTruncated(Boolean(data?.events_truncated));
};

/**
 * 팝업이 열릴 때 공정 이벤트·재고 이동을 1회 조회한다.
 * 더 보기는 재고 items 만 이어 붙이고 이벤트는 첫 응답을 유지한다.
 */
export function useSubOrderProcessActivity({ open, subOrderId, dyeingNoShort }) {
  const { t } = useTranslation();
  const { showError } = useError();
  const [dyeingEvents, setDyeingEvents] = useState([]);
  const [windingEvents, setWindingEvents] = useState([]);
  const [eventsTruncated, setEventsTruncated] = useState(false);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [itemMap, setItemMap] = useState(() => new Map());
  const [locationMap, setLocationMap] = useState(() => new Map());
  const [uomMap, setUomMap] = useState(() => new Map());

  const offsetRef = useRef(0);
  const hasMoreRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const queryRef = useRef({ subOrderId, dyeingNoShort });
  const showErrorRef = useRef(showError);
  const tRef = useRef(t);
  queryRef.current = { subOrderId, dyeingNoShort };
  showErrorRef.current = showError;
  tRef.current = t;

  const notifyFailure = useCallback((result, { emptyOn404, messageKey }) => {
    const code = result?.error?.code;
    if (code === 401) return;
    if (code === 404) {
      if (emptyOn404) {
        setDyeingEvents([]);
        setWindingEvents([]);
        setEventsTruncated(false);
        setItems([]);
        setHasMore(false);
        hasMoreRef.current = false;
        offsetRef.current = 0;
        setLoaded(true);
      }
      return;
    }
    const serverMessage = result?.error?.message;
    const message = (code === 400 || code === 409) && serverMessage
      ? serverMessage
      : tRef.current(messageKey || 'orders.subOrder.detail.processActivityLoadFailed');
    showErrorRef.current({ message });
  }, []);

  const loadMaps = useCallback(async () => {
    const maps = await buildLookupMaps();
    setItemMap(maps.itemMap);
    setLocationMap(maps.locationMap);
    setUomMap(maps.uomMap);
  }, []);

  useEffect(() => {
    const hasKey = hasText(dyeingNoShort) || (subOrderId != null && subOrderId !== '');
    if (!open || !hasKey) {
      setDyeingEvents([]);
      setWindingEvents([]);
      setEventsTruncated(false);
      setItems([]);
      setHasMore(false);
      hasMoreRef.current = false;
      offsetRef.current = 0;
      setLoaded(false);
      setLoading(false);
      setLoadingMore(false);
      return undefined;
    }

    let cancelled = false;
    setLoaded(false);
    setDyeingEvents([]);
    setWindingEvents([]);
    setEventsTruncated(false);
    setItems([]);
    setHasMore(false);
    hasMoreRef.current = false;
    offsetRef.current = 0;
    setLoading(true);

    (async () => {
      try {
        await loadMaps();
        if (cancelled) return;
        const result = await orderService.getProcessActivity(
          buildProcessActivityArgs(subOrderId, dyeingNoShort, 0)
        );
        if (cancelled) return;
        if (!result?.success) {
          notifyFailure(result, { emptyOn404: true });
          if (result?.error?.code !== 404) {
            setLoaded(true);
          }
          return;
        }
        const pageItems = Array.isArray(result.data?.items) ? result.data.items : [];
        applyEvents(result.data, { setDyeingEvents, setWindingEvents, setEventsTruncated });
        setItems(pageItems);
        const more = Boolean(result.data?.has_more);
        setHasMore(more);
        hasMoreRef.current = more;
        offsetRef.current = pageItems.length;
        setLoaded(true);
      } catch (err) {
        if (!cancelled) {
          notifyFailure({ error: { code: 0, message: err?.message } }, { emptyOn404: false });
          setLoaded(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, subOrderId, dyeingNoShort, loadMaps, notifyFailure]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || !hasMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const { subOrderId: id, dyeingNoShort: no } = queryRef.current;
      const result = await orderService.getProcessActivity(
        buildProcessActivityArgs(id, no, offsetRef.current)
      );
      if (!result?.success) {
        notifyFailure(result, {
          emptyOn404: false,
          messageKey: 'orders.subOrder.detail.inventoryTx.loadFailed'
        });
        return;
      }
      const pageItems = Array.isArray(result.data?.items) ? result.data.items : [];
      setItems((prev) => [...prev, ...pageItems]);
      const more = Boolean(result.data?.has_more);
      setHasMore(more);
      hasMoreRef.current = more;
      offsetRef.current += pageItems.length;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [notifyFailure]);

  const refreshActivity = useCallback(async () => {
    const { subOrderId: id, dyeingNoShort: no } = queryRef.current;
    if (!hasText(no) && (id == null || id === '')) return;
    setLoading(true);
    try {
      await loadMaps();
      const result = await orderService.getProcessActivity(buildProcessActivityArgs(id, no, 0));
      if (!result?.success) {
        notifyFailure(result, { emptyOn404: true });
        return;
      }
      const pageItems = Array.isArray(result.data?.items) ? result.data.items : [];
      applyEvents(result.data, { setDyeingEvents, setWindingEvents, setEventsTruncated });
      setItems(pageItems);
      const more = Boolean(result.data?.has_more);
      setHasMore(more);
      hasMoreRef.current = more;
      offsetRef.current = pageItems.length;
      setLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [loadMaps, notifyFailure]);

  return {
    dyeingEvents,
    windingEvents,
    eventsTruncated,
    loading,
    loaded,
    refreshActivity,
    inventory: {
      items,
      hasMore,
      loading,
      loadingMore,
      loaded,
      itemMap,
      locationMap,
      uomMap,
      loadMore,
      refresh: refreshActivity
    }
  };
}

const cellSx = { fontSize: '0.75rem' };

const TruncationNotice = () => {
  const { t } = useTranslation();
  return (
    <Typography sx={{ fontSize: '0.75rem', color: '#b45309', mb: 1 }}>
      {t('orders.subOrder.detail.eventsTruncated')}
    </Typography>
  );
};

const EventStatus = ({ loading, loaded, count }) => {
  const { t } = useTranslation();
  if (count > 0) return null;
  if (!loaded || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  return (
    <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {t('orders.subOrder.detail.noEvent')}
      </Typography>
    </Box>
  );
};

export const DyeingProcessEventList = ({ events, eventsTruncated, loading, loaded, formatDate }) => {
  const { t } = useTranslation();
  const rows = events || [];
  if (rows.length === 0) {
    return (
      <>
        {eventsTruncated && loaded && <TruncationNotice />}
        <EventStatus loading={loading} loaded={loaded} count={0} />
      </>
    );
  }

  return (
    <>
      {eventsTruncated && <TruncationNotice />}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem' } }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('orders.subOrder.detail.work')}</TableCell>
              <TableCell>{t('orders.subOrder.detail.eventName')}</TableCell>
              <TableCell>{t('orders.subOrder.detail.author')}</TableCell>
              <TableCell>{t('orders.subOrder.detail.value1')}</TableCell>
              <TableCell>{t('orders.subOrder.detail.value2')}</TableCell>
              <TableCell>{t('orders.subOrder.detail.remark')}</TableCell>
              <TableCell>{t('orders.subOrder.detail.eventTime')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((event, index) => (
              <TableRow key={event.id ?? index}>
                <TableCell sx={cellSx}>{event.work_label || '-'}</TableCell>
                <TableCell sx={cellSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <span>{event.event_name || '-'}</span>
                    {event.shared_run && (
                      <Chip
                        size="small"
                        color="info"
                        label={t('orders.subOrder.detail.sharedRun')}
                        title={t('orders.subOrder.detail.sharedRunHint')}
                        sx={{ height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={cellSx}>{event.created_by || '-'}</TableCell>
                <TableCell sx={cellSx}>{event.value_1_str || '-'}</TableCell>
                <TableCell sx={cellSx}>{event.value_2_str || '-'}</TableCell>
                <TableCell sx={cellSx}>{event.note || '-'}</TableCell>
                <TableCell sx={cellSx}>{formatDate(event.event_time || event.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export const WindingProcessEventList = ({ events, eventsTruncated, loading, loaded, formatDate }) => {
  const { t } = useTranslation();
  const rows = events || [];
  if (rows.length === 0) {
    return (
      <>
        {eventsTruncated && loaded && <TruncationNotice />}
        <EventStatus loading={loading} loaded={loaded} count={0} />
      </>
    );
  }

  return (
    <>
      {eventsTruncated && <TruncationNotice />}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.75rem' } }}>
          <TableHead>
            <TableRow>
              <TableCell>{t('orders.subOrder.detail.work')}</TableCell>
              <TableCell>{t('orders.subOrder.detail.eventName')}</TableCell>
              <TableCell>{t('orders.subOrder.detail.author')}</TableCell>
              <TableCell>{t('orders.subOrder.detail.value')}</TableCell>
              <TableCell>{t('orders.subOrder.detail.remark')}</TableCell>
              <TableCell>{t('orders.subOrder.detail.eventTime')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((event, index) => (
              <TableRow key={event.id ?? index}>
                <TableCell sx={cellSx}>{event.work_label || '-'}</TableCell>
                <TableCell sx={cellSx}>{event.event_name || '-'}</TableCell>
                <TableCell sx={cellSx}>{event.created_by || '-'}</TableCell>
                <TableCell sx={cellSx}>{event.value_1_str || '-'}</TableCell>
                <TableCell sx={cellSx}>{event.note || '-'}</TableCell>
                <TableCell sx={cellSx}>{formatDate(event.event_time || event.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export const SubOrderInventoryTxPanel = ({
  open,
  resetKey,
  items,
  hasMore,
  loading,
  loadingMore,
  loaded,
  itemMap,
  locationMap,
  uomMap,
  onLoadMore,
  onRefresh
}) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const language = (i18n.language || 'ko').split('-')[0];

  useEffect(() => {
    setExpanded(false);
  }, [open, resetKey]);

  return (
    <Box
      sx={{
        mt: 2,
        mb: 1,
        border: '1px solid #94a3b8',
        bgcolor: '#ffffff',
        alignSelf: 'stretch',
        width: '100%'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#475569',
          color: '#fff',
          py: 0.75,
          px: 1.5,
          cursor: 'pointer',
          userSelect: 'none'
        }}
        onClick={() => setExpanded((value) => !value)}
      >
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
          {t('orders.subOrder.detail.inventoryTx.title')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {expanded && (
            <IconButton
              size="small"
              sx={{ color: '#fff' }}
              disabled={loading}
              onClick={(event) => {
                event.stopPropagation();
                onRefresh();
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          )}
          <ExpandMoreIcon
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}
          />
        </Box>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: 1.5, bgcolor: '#f8fafc' }}>
          {loading && items.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          )}
          {!loading && loaded && items.length === 0 && (
            <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>
              {t('orders.subOrder.detail.inventoryTx.empty')}
            </Typography>
          )}
          {items.length > 0 && (
            <Box sx={{ overflowX: 'auto', border: '1px solid #cbd5e1', bgcolor: '#fff' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#e2e8f0' }}>
                    <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {t('dyeing.recipe.inventoryTx.columns.txTime')}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {t('orders.subOrder.detail.inventoryTx.workType')}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {t('orders.subOrder.detail.inventoryTx.work')}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {t('dyeing.recipe.inventoryTx.columns.txType')}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {t('dyeing.recipe.inventoryTx.columns.item')}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}
                    >
                      {t('dyeing.recipe.inventoryTx.columns.qty')}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {t('dyeing.recipe.inventoryTx.columns.uom')}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {t('dyeing.recipe.inventoryTx.columns.from')}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {t('dyeing.recipe.inventoryTx.columns.to')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((row) => {
                    const info = itemMap.get(Number(row.item_id));
                    const itemLabel = info
                      ? `${info.sku ? `${info.sku} ` : ''}${info.name || ''}`.trim() || String(row.item_id)
                      : String(row.item_id ?? '—');
                    const isVoid = String(row?.tx_subtype || '').toLowerCase() === 'void';
                    const rowCellSx = {
                      fontSize: '0.72rem',
                      whiteSpace: 'nowrap',
                      ...(isVoid ? { textDecoration: 'line-through', color: '#94a3b8' } : {})
                    };
                    return (
                      <TableRow
                        key={row.tx_id}
                        hover={!isVoid}
                        sx={isVoid ? { bgcolor: '#f8fafc' } : undefined}
                      >
                        <TableCell sx={rowCellSx}>{formatTxTime(row.tx_time, language)}</TableCell>
                        <TableCell sx={rowCellSx}>{row.work_type || '—'}</TableCell>
                        <TableCell sx={rowCellSx}>{row.work_label || '—'}</TableCell>
                        <TableCell sx={rowCellSx}>{row.tx_type || '—'}</TableCell>
                        <TableCell sx={{ ...rowCellSx, whiteSpace: 'normal' }}>{itemLabel}</TableCell>
                        <TableCell align="right" sx={rowCellSx}>
                          {row.qty != null ? String(row.qty) : '—'}
                        </TableCell>
                        <TableCell sx={rowCellSx}>
                          {uomMap.get(Number(row.uom_id)) || row.uom_id || '—'}
                        </TableCell>
                        <TableCell sx={rowCellSx}>
                          {formatLocationLabel(row.from_location_id, locationMap)}
                        </TableCell>
                        <TableCell sx={rowCellSx}>
                          {formatLocationLabel(row.to_location_id, locationMap)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
              <Button size="small" variant="outlined" disabled={loadingMore || loading} onClick={onLoadMore}>
                {loadingMore ? <CircularProgress size={16} /> : t('orders.subOrder.detail.inventoryTx.loadMore')}
              </Button>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
